import React from 'react';
import { Card, Result, Button, Descriptions, Alert, Space } from 'antd';
import { CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';

interface PaymentFailureProps {
  transactionId: string;
  code: number;
  messages: string[];
  messageCodes: string[];
  account: string;
  amount: number;
  phone?: string;
  promo?: string;
  onRetry: () => void;
  onCancel: () => void;
}

const PaymentFailure: React.FC<PaymentFailureProps> = ({
  transactionId,
  code,
  messages,
  messageCodes,
  account,
  amount,
  phone,
  promo,
  onRetry,
  onCancel,
}) => {
  // Determine error severity and title based on code
  const isServerError = code >= 500;
  const isClientError = code >= 400 && code < 500;

  const getErrorTitle = () => {
    if (code === 429 && messageCodes.includes('ERR_ACCOUNT_DAILY_LIMIT_EXCEEDED')) {
      return 'Превышен дневной лимит платежей';
    }
    if (code === 406) return 'Платеж не может быть обработан';
    if (code === 400) return 'Неверный запрос платежа';
    if (code === 404) return 'Сервис платежей не найден';
    if (isServerError) return 'Ошибка сервера';
    return 'Платеж не выполнен';
  };

  const getErrorIcon = () => {
    if (isServerError) {
      return <WarningOutlined style={{ color: '#ff4d4f' }} />;
    }
    return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
  };

  return (
    <Card
      style={{
        width: '100%',
        maxWidth: 500,
      }}
    >
      <Result
        icon={getErrorIcon()}
        status="error"
        title={getErrorTitle()}
        subTitle="Ваш платеж не был выполнен. Пожалуйста, ознакомьтесь с деталями ошибки ниже."
        extra={[
          <Button type="primary" size="large" onClick={onRetry} key="retry">
            Попробовать снова
          </Button>,
          <Button size="large" onClick={onCancel} key="cancel">
            Отменить
          </Button>,
        ]}
      />

      {/* Error Messages */}
      <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 24 }}>
        {messages?.map((msg, index) => (
          <Alert
            key={index}
            message={`Ошибка: ${messageCodes?.[index] || 'UNKNOWN'}`}
            description={msg}
            type="error"
            showIcon
          />
        ))}
      </Space>

      {/* Payment Details */}
      <Descriptions
        bordered
        column={1}
        size="small"
        title="Детали платежа"
      >
        <Descriptions.Item label="ID транзакции">
          <code style={{ fontSize: '12px', color: '#ff4d4f' }}>{transactionId}</code>
        </Descriptions.Item>
        <Descriptions.Item label="Код статуса">
          <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
            {code}
          </span>
        </Descriptions.Item>
        <Descriptions.Item label="Аккаунт">
          <strong>{account}</strong>
        </Descriptions.Item>
        <Descriptions.Item label="Сумма">
          <strong style={{ fontSize: '16px' }}>
            {amount} ₸
          </strong>
        </Descriptions.Item>
        {phone && (
          <Descriptions.Item label="Телефон">
            {phone}
          </Descriptions.Item>
        )}
        {promo && (
          <Descriptions.Item label="Промо-код">
            <span style={{ color: '#1890ff' }}>
              {promo}
            </span>
          </Descriptions.Item>
        )}
      </Descriptions>

      {/* Help Information */}
      <div
        style={{
          marginTop: 24,
          padding: '12px',
          background: '#fff7e6',
          borderRadius: '8px',
          border: '1px solid #ffd591',
        }}
      >
        <Space direction="vertical" size="small">
          <span style={{ fontSize: '12px', color: '#d46b08' }}>
            💡 <strong>Что делать:</strong>
          </span>
          <ul style={{ fontSize: '12px', color: '#8c8c8c', margin: '8px 0 0 16px', paddingLeft: '8px' }}>
            {code === 429 && messageCodes.includes('ERR_ACCOUNT_DAILY_LIMIT_EXCEEDED') && (
              <>
                <li>Достигнут максимальный лимит платежей в день для данного аккаунта</li>
                <li>Попробуйте совершить платеж завтра</li>
                <li>Используйте другой аккаунт, если доступен</li>
                <li>Свяжитесь с поддержкой для повышения лимита</li>
              </>
            )}
            {code === 406 && (
              <>
                <li>Проверьте, достаточно ли средств на вашем счете</li>
                <li>Попробуйте с меньшей суммой</li>
                <li>Свяжитесь с поддержкой, если проблема не исчезнет</li>
              </>
            )}
            {code === 400 && (
              <>
                <li>Убедитесь, что номер аккаунта верный</li>
                <li>Проверьте, что сумма корректна</li>
                <li>Убедитесь, что все обязательные поля заполнены</li>
              </>
            )}
            {isServerError && (
              <>
                <li>Подождите несколько минут и попробуйте снова</li>
                <li>Если проблема не исчезнет, свяжитесь с поддержкой</li>
                <li>Сохраните ID транзакции для справки</li>
              </>
            )}
            {!isServerError && code !== 400 && code !== 406 && code !== 429 && (
              <>
                <li>Ознакомьтесь с сообщением об ошибке выше</li>
                <li>Исправьте проблемы и попробуйте снова</li>
                <li>Свяжитесь с поддержкой, если нужна помощь</li>
              </>
            )}
          </ul>
        </Space>
      </div>
    </Card>
  );
};

export default PaymentFailure;
